import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';

import { forkJoin, Observable, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

import { Logger } from '@sondermind/datadog-logging';
import { Attestation, AttestationDataService } from '@sondermind/data-access/attestation';
import { CreditCardAddComponent } from '@sondermind/features/account-edit';
import { FAQAccordionModule } from '@sondermind/features/faq-accordion';
import { IrisButtonModule, IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisCheckboxModule } from '@sondermind/iris/checkbox';
import { IrisSpinnerComponent } from '@sondermind/iris/spinner';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { AccountSetupService } from '@sondermind/utilities/services';
import { InfoLinks } from '@sondermind/core/enums/info-links.enum';

@Component({
  selector: 'client-setup-payment-info',
  templateUrl: './setup-payment-info.component.html',
  styleUrls: ['./setup-payment-info.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CreditCardAddComponent,
    FAQAccordionModule,
    IrisButtonModule,
    IrisCheckboxModule,
    IrisSpinnerComponent
  ]
})
export class SetupPaymentInfoComponent {
  // Inputs
  readonly useInSessionRequest = input(false);
  readonly isMobile = input(false);
  readonly isProposeATime = input(false);
  readonly showPaymentFaq = input(false);
  readonly fromDirectBooking = input(false);

  // Outputs
  readonly paymentInfoSubmitted = output<void>();

  // View children
  readonly ccFormComponent = viewChild.required<CreditCardAddComponent>('ccForm');
  readonly actionButtonTemplate = viewChild.required<TemplateRef<unknown>>('actionButton');

  // Dependency injection
  private readonly attestationDataService = inject(AttestationDataService);
  private readonly accountSetupService = inject(AccountSetupService);
  private readonly fb = inject(FormBuilder);
  private readonly logger = inject(Logger);

  // Constants
  protected readonly financialResponsibilityAgreement = InfoLinks.FINANCIAL_RESPONSIBILITY_AGREEMENT;
  protected readonly irisButtonContainerAlignments = IrisButtonContainerAlignments;
  protected readonly irisButtonTypes = IrisButtonTypes;
  protected readonly irisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;

  // State
  protected readonly submittingFinancialPolicy = signal(false);

  // Form (public for tests)
  readonly paymentForm = this.fb.group({
    acceptedFinancialPolicy: [false as boolean | null]
  });

  /**
   * Enable submit button on new client setup when CC Form is complete, not currently
   * submitting, and client has checked box accepting financial policy
   * Note: Using getter instead of computed because form values aren't reactive
   */
  protected get allowSubmit(): boolean {
    const ccForm = this.ccFormComponent();
    if (!ccForm) {
      return false;
    }
    const isPolicyAccepted: boolean = this.paymentForm.value.acceptedFinancialPolicy === true;
    return ccForm.formValid()
      && !ccForm.submitting()
      && !this.submittingFinancialPolicy()
      && isPolicyAccepted;
  }

  /**
   * Submit child credit card form. Only credit card can be added during new client setup.
   * When form succeeds, event emitted triggers submitFinancialAgreements
   */
  submitCreditCard(): void {
    const ccForm = this.ccFormComponent();
    if (ccForm) {
      ccForm.saveNewCard();
    }
  }

  /**
   * Combine into list of attestations sign requests and return forkJoin of list.
   * Triggered after adding first credit card.
   */
  submitFinancialAgreements(): void {
    this.startSubmitting();

    this.attestationDataService.refresh().pipe(
      switchMap(
        (resp: Attestation[]) => {
          // filter to financial agreement only
          const filtered: Attestation[] = resp.filter((att) => att.agreedAt == null && att.documentTitle.includes('Financial'));
          const reqs: Array<Observable<void>> = filtered.map(
            (att) => this.attestationDataService.markAgreed(att.id, this.accountSetupService.context ?? undefined)
          );

          // if attestations have all been signed, dont forkJoin
          if (reqs.length === 0) {
            return of(undefined);
          }

          return forkJoin(reqs);
        }
      ),
      finalize(() => this.stopSubmitting())
    ).subscribe({
      next: () => {
        this.paymentInfoSubmitted.emit();
      },
      error: (error) => {
        this.logger.error('Failed to submit financial agreements', { error });
      }
    });
  }

  /**
   * Wrapper for unit tests.
   */
  startSubmitting(): void {
    this.submittingFinancialPolicy.set(true);
  }

  /**
   * Wrapper for unit tests.
   */
  stopSubmitting(): void {
    this.submittingFinancialPolicy.set(false);
  }
}
